import { Eye, ShieldCheck } from "lucide-react";

import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { useAuthStore } from "@/stores/auth";
import {
  ROLE_LABELS,
  assumableRoles,
  getActAsRole,
  setActAsRole,
} from "@/lib/actAsRole";
import { cn } from "@/lib/utils";

/**
 * Lets a human tester run our test cases as a lower-privilege role.
 *
 * Bob: "we need the ability to change the user role from a drop down in the UI
 * so the testers can actually do the tests we have created." Our manual suite
 * has 83 precondition clauses naming a role or permission, and every tester
 * account holds ORG_ADMIN — so without this, those cases cannot be run at all.
 *
 * It is a DOWNGRADE, never a grant: `assumableRoles` offers only roles at or
 * below the rank the user genuinely holds, and the backend
 * (`narrow_roles_for_act_as`) ignores anything else. Rendering a role the user
 * does not outrank would produce a control that appears to work while changing
 * nothing — the precise class of defect this feature exists to let testers find.
 *
 * WHY A FULL RELOAD ON CHANGE. Effective role is baked into data already
 * fetched and cached — nav items, project lists, permission-shaped responses.
 * Swapping the header without re-fetching would leave a tester looking at
 * admin-shaped data while believing they are a viewer, and they would file the
 * absence of a restriction as a defect. Reload is what OrgSwitcher does, for
 * the same reason.
 *
 * WHY THE CONTROL IS LOUD WHEN ACTIVE. An assumed role that is not obvious is
 * worse than no feature: a tester who forgets they are VIEWER reports every
 * hidden button as broken. Downgraded state gets an accent treatment, its own
 * icon, and `aria-live` so it is announced, not merely visible.
 */
export function ActAsRoleSwitcher() {
  const user = useAuthStore((s) => s.user);
  // sessionStorage is read once per mount; every change reloads the page, so
  // there is no second source of truth to drift from.
  const active = getActAsRole();

  if (!user) return null;

  const held = (user.roles ?? []).map((r) => r.toUpperCase());
  const options = assumableRoles(held);
  // One option means their real role is the only thing they can be — the
  // control would be inert, so it is not drawn at all.
  if (options.length <= 1) return null;

  const realRole = options[0];
  const current = active && options.includes(active) ? active : realRole;
  const downgraded = current !== realRole;

  const handleChange = (role: string) => {
    if (role === current) return;
    setActAsRole(role === realRole ? null : role);
    // Full reload: re-scopes every cached query and store to the new
    // effective role. See the note above on why a header swap is not enough.
    window.location.reload();
  };

  return (
    <>
      <Select value={current} onValueChange={handleChange}>
        <SelectTrigger
          aria-label="Act as role"
          data-testid="act-as-role-switcher"
          data-downgraded={downgraded ? "true" : "false"}
          className={cn(
            "h-9 w-auto gap-1.5 border-[var(--color-border)] px-2.5 text-xs",
            // Amber is the BORDER AND GROUND ONLY. It is not used for the
            // label or the glyph: #f59e0b on --color-warning-bg measures
            // 2.07:1 in light mode, well under AA, and a tester squinting at
            // their own privilege level is the last person to shortchange.
            // Text stays --color-text-primary, which measures 17.2:1 light
            // and 14.8:1 dark on this ground. The downgrade is carried by the
            // role NAME in the label, so the tint is reinforcement, never the
            // sole signal.
            downgraded &&
              "border-[var(--color-warning)] bg-[var(--color-warning-bg)] text-[var(--color-text-primary)]",
          )}
        >
          {downgraded ? (
            <Eye className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          ) : (
            <ShieldCheck className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          )}
          {/* The trigger names the role alone; " — your role" belongs in the
              list. The suffix made the pill wide enough to cut the page title
              to "Keystone Valley OnBase Implemen…" at 1600px (row 8f61634b). */}
          <SelectValue>{ROLE_LABELS[current] ?? current}</SelectValue>
        </SelectTrigger>
        <SelectContent>
          {options.map((role) => (
            <SelectItem key={role} value={role} className="text-xs">
              {ROLE_LABELS[role] ?? role}
              {role === realRole ? " — your role" : ""}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {/* Outside <Select> on purpose: Radix expects only Trigger and Content
          as its children, and a stray node there is a rendering hazard. */}
      <span className="sr-only" role="status" aria-live="polite">
        {downgraded
          ? `Acting as ${ROLE_LABELS[current] ?? current}. This is a reduced-privilege view for testing.`
          : `Acting as your own role, ${ROLE_LABELS[current] ?? current}.`}
      </span>
    </>
  );
}
